// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { Account } from '../../../shared-api/account';
import { AuthFlows } from '../../../shared-api/authFlows';
import { AuthStageName } from '../../../shared-api/authStageName';
import { AccountService as ApiAccountService } from '../../../shared-api/account.service';
import { getUsernameFromEncodedMatrixId } from '../../../../common/helpers/encoding.helper';
import { MatrixError } from 'matrix-js-sdk';
import { matrixErrorAsJson } from '../../errors/error-with-status';
import { catchError, from, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
/**
 * Der AccountService stellt den U bereit.
 * Implementation of the mandatory Account API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class AccountService implements ApiAccountService {
  private readonly matrix = inject(MatrixService);

  public isLoggedIn(mxId: string): boolean {
    if (!mxId) {
      throw new Error(`No mxId was passed`);
    }
    return mxId === this.getMyMxId();
  }

  /**
   * Returns the account data of the currently logged-in user.
   * Returns the authentication flows supported by the homeserver if no user is logged in.
   */
  async readAccount(isOrgAdminTT = false): Promise<Account | AuthFlows> {
    let mxid: string | null;
    if (this.matrix.hasClient && (mxid = this.matrix.getUserId())) {
      try {
        const profile = await this.matrix.client.getProfileInfo(mxid);
        const accessToken = this.matrix.client.getAccessToken();

        return {
          mxid,
          displayName: profile.displayname ?? '',
          username: getUsernameFromEncodedMatrixId(mxid),
          password: '',
          accessToken: accessToken
        } as Account;
      } catch (error) {
        console.error('getProfileInfo fehlgeschlagen', error);

        if (!(error instanceof MatrixError)) {
          throw error;
        }
        throw matrixErrorAsJson('GetProfileInfoError', error);
      }
    } else if (isOrgAdminTT) {
      // we have to use the Account-Model, but re-Login is for OrgAdmin not necessary
      return {
        mxid: '',
        displayName: 'OrgAdmin',
        username: 'dummy',
        password: ''
      } as Account;
    } else {
      return {
        completedAuthStages: [],
        flows: [{ stages: [AuthStageName.BasicAuth] }],
        params: {}
      } as AuthFlows;
    }
  }

  /** Returns the displayname of the currently logged-in user. */
  getUserDisplayName(): Observable<string> {
    const userId = this.getMyMxId();
    return from(this.matrix.client.getProfileInfo(userId, 'displayname')).pipe(
      map(profile => profile.displayname || userId),
      catchError(error => {
        console.error('Failed to fetch display name', error);
        return of(userId);
      })
    );
  }

  getMyMxId(): string {
    const userId = this.matrix.client.getUserId();
    if (!userId) {
      throw new Error('No userId was found when calling getMyMxId');
    }
    return userId;
  }
}
